import type { CallStatus, TechnicalResult } from '@autodialer/shared';

import { api } from '../api/client';

/** Telefonie-Endpunkte des Backends. */

export interface CallSummary {
  id: string;
  campaignId: string;
  contactId: string | null;
  phoneNumber: string;
  status: CallStatus;
  startedAt: string;
  /** Serverseitiger Zeitpunkt - die Anzeige rechnet daraus die Dauer. */
  connectedAt: string | null;
  technicalResult: TechnicalResult | null;
  requiresWrapUp: boolean;
}

export interface PhoneRegistration {
  username: string;
  password: string;
  endpointId: string;
  websocketUrl: string;
  domain: string;
}

export interface WrapUpInput {
  salesResult?: string;
  note?: string | null;
  callbackDate?: string | null;
  fields?: Record<string, string>;
}

const callPath = (callId: string): string => '/telephony/calls/' + callId;

export const telephonyApi = {
  register: () => api.post<{ registration: PhoneRegistration }>('/telephony/registration'),

  start: (input: {
    campaignId: string;
    contactId?: string | null;
    phoneNumber?: string | null;
    clientRequestId: string;
  }) => api.post<{ call: CallSummary }>('/telephony/calls', input),

  get: (callId: string) => api.get<{ call: CallSummary }>(callPath(callId)),
  hangup: (callId: string) => api.post<{ ok: true }>(callPath(callId) + '/hangup'),
  mute: (callId: string, muted: boolean) => api.post<{ ok: true }>(callPath(callId) + '/mute', { muted }),
  reattach: (callId: string) => api.post<{ call: CallSummary | null }>(callPath(callId) + '/reattach'),
  wrapUp: (callId: string, input: WrapUpInput) => api.post<{ ok: true }>(callPath(callId) + '/wrap-up', input),
};

export interface SipConfigView {
  host: string;
  username: string;
  hasPassword: boolean;
  port: number | null;
  transport: 'udp' | 'tcp' | 'tls' | null;
  authId: string | null;
  realm: string | null;
  userId?: string;
}

export const sipApi = {
  get: (campaignId: string) =>
    api.get<{ global: SipConfigView | null; individual: SipConfigView[] }>(
      '/campaigns/' + campaignId + '/sip',
    ),
  saveGlobal: (campaignId: string, input: Record<string, unknown>) =>
    api.put<{ config: SipConfigView }>('/campaigns/' + campaignId + '/sip', input),
  saveForUser: (campaignId: string, userId: string, input: Record<string, unknown>) =>
    api.put<{ config: SipConfigView }>('/campaigns/' + campaignId + '/sip/users/' + userId, input),
  test: (campaignId: string, userId: string | null) =>
    api.post<{ result: 'ok' | 'rejected' | 'unreachable' }>('/campaigns/' + campaignId + '/sip/test', {
      userId,
    }),
};

/** Zustände eines Callers im Dialer (13_architecture.md). */
export type CallerStatus =
  | 'OFFLINE'
  | 'ONLINE'
  | 'AVAILABLE'
  | 'DIALING'
  | 'RINGING'
  | 'IN_CALL'
  | 'WRAP_UP'
  | 'PAUSED'
  | 'RECONNECTING';

export interface CampaignRuntime {
  busy: boolean;
  waiting: number;
  callers: { userId: string; status: CallerStatus; since: string }[];
  calls?: Record<string, number>;
}

/**
 * Der Dialer.
 *
 * Bereitschaft ist immer eine bewusste Handlung - angemeldet sein genügt
 * ausdrücklich nicht (05_dialer.md).
 */
export const dialerApi = {
  ready: (campaignId: string) =>
    api.post<{ status: CallerStatus }>('/telephony/campaigns/' + campaignId + '/ready'),
  pause: (campaignId: string) =>
    api.post<{ status: CallerStatus }>('/telephony/campaigns/' + campaignId + '/pause'),
  stop: (campaignId: string) =>
    api.post<{ status: CallerStatus }>('/telephony/campaigns/' + campaignId + '/stop'),

  /** Der eigene Zustand - nach einem Neuladen der Seite. */
  me: () => api.get<{ status: CallerStatus; campaignId: string | null }>('/telephony/me'),

  /** Zustand der Kampagne - nur für Administratoren. */
  campaignState: (campaignId: string) =>
    api.get<CampaignRuntime>('/telephony/campaigns/' + campaignId + '/state'),
};
